import React, { useCallback, useEffect, useState } from 'react';
import { Button, Card, Col, Empty, Row, Space, Spin, Tag, Typography, message } from 'antd';
import { PlusOutlined, ReloadOutlined, SendOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../../shared/i18n';
import { userPath } from '../../../shared/config/paths';
import PageHeader from '../../../shared/components/PageHeader';
import tmService from '../../services/tmService';
import NewStreamModal from './NewStreamModal';
import TrustCard from './components/TrustCard';
import { SandboxTag, StreamStatusTag, StreamTypeTag, UsageBar, ok, toastErr } from './components/common';

const { Text } = Typography;

/** 释放沙箱：后端判定条件（账号信用等级 ≥ Lv2），不满足返回 409 + 原因 */
export const releaseSandbox = async (stream, t, done) => {
    try {
        const r = await tmService.releaseSandbox(stream.id);
        if (ok(r)) {
            message.success(r.message && r.message !== 'success' ? r.message : t('Sandbox lifted'));
            done && done();
        }
    } catch (e) {
        toastErr(e, t('Sandbox can\'t be lifted yet'), true);
    }
};

const Streams = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [streams, setStreams] = useState([]);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [trust, setTrust] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await tmService.listStreams();
            setStreams(Array.isArray(r?.data) ? r.data : []);
            tmService.getTrust().then((tr) => setTrust(tr?.data || null)).catch(() => setTrust(null));
        } catch (e) {
            toastErr(e, t('Failed to load'));
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => { load(); }, [load]);

    return (
        <div>
            <PageHeader
                title={t('Email sending')}
                icon={<SendOutlined />}
                extra={
                    <Space>
                        <Button icon={<ReloadOutlined />} onClick={load} loading={loading} />
                        <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreating(true)}>{t('New stream')}</Button>
                    </Space>
                }
            />
            <Spin spinning={loading}>
                {streams.length > 0 && <TrustCard trust={trust} streams={streams} />}
                {!loading && streams.length === 0 ? (
                    <Card>
                        <Empty description={t('No sending streams yet')}>
                            <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreating(true)}>{t('New stream')}</Button>
                        </Empty>
                    </Card>
                ) : (
                    <Row gutter={[16, 16]}>
                        {streams.map((s) => (
                            <Col xs={24} md={12} xl={8} key={s.id}>
                                <Card hoverable onClick={() => navigate(userPath(`/tm/streams/${s.id}`))} styles={{ body: { padding: 16 } }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
                                        <div style={{ minWidth: 0 }}>
                                            <Text strong style={{ fontSize: 16 }} ellipsis>{s.name}</Text>
                                            <div style={{ marginTop: 2 }}>
                                                <Text type="secondary" style={{ fontSize: 12 }}>{s.plan ? t(s.plan.name) : '-'}</Text>
                                            </div>
                                        </div>
                                        <Space size={4} wrap style={{ justifyContent: 'flex-end' }}>
                                            <StreamTypeTag type={s.type} />
                                            <StreamStatusTag stream={s} />
                                            {s.sandbox && <SandboxTag />}
                                        </Space>
                                    </div>
                                    {(s.status === 'paused' || s.status === 'banned') && (s.paused_reason || s.banned_reason) && (
                                        <div style={{ marginTop: 8 }}>
                                            <Text type="danger" style={{ fontSize: 12 }}>{s.status === 'banned' ? s.banned_reason : s.paused_reason}</Text>
                                        </div>
                                    )}
                                    <div style={{ marginTop: 12 }}><UsageBar usage={s.usage} /></div>
                                    <div style={{ marginTop: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                                        <Text type="secondary" style={{ fontSize: 12 }} ellipsis>{s.smtp_host}</Text>
                                        {s.sandbox_releasable && s.status === 'active' && (
                                            <Button size="small" onClick={(e) => { e.stopPropagation(); releaseSandbox(s, t, load); }}>{t('Leave sandbox')}</Button>
                                        )}
                                        {s.subscription_status && s.subscription_status !== 'active' && (
                                            <Tag color="orange" style={{ marginInlineEnd: 0 }}>{t('Subscription')}: {t(s.subscription_status)}</Tag>
                                        )}
                                    </div>
                                </Card>
                            </Col>
                        ))}
                    </Row>
                )}
            </Spin>
            <NewStreamModal open={creating} onClose={() => setCreating(false)} />
        </div>
    );
};

export default Streams;
